import React, { useState, useEffect, useRef, useCallback } from 'react'
import { inject, observer, Provider } from 'mobx-react'
import { withTranslation } from 'react-i18next'
import { Marker, Tooltip } from 'react-leaflet'
import L from 'leaflet'
import MarkerModel from '../../stores/Marker'
import PointBuffer from '../../stores/PointBuffer'
import Isochrone from '../../stores/Isochrone'
import MarkerPane from '../tools/panes/MarkerPane'
import PointBufferPane from '../tools/panes/PointBufferPane'
import IsochronePane from '../tools/panes/IsochronePane'
import Icon from '../common/Icon'
import { uiStore as defaultUiStore, dataStore as defaultDataStore, selectionStore as defaultSelectionStore } from '../../stores/UIStore'

const quickCreateMarkerIcon = L.divIcon({
  className: 'quick-create-map-pin',
  html: '<div class="quick-create-map-pin__dot"></div>',
  iconSize: [16, 16],
  iconAnchor: [8, 8]
})

const QuickCreateTooltip = ({ uiStore, selectionStore, dataStore, t }) => {
  const currentUiStore = uiStore || defaultUiStore
  const currentDataStore = dataStore || defaultDataStore
  const currentSelectionStore = selectionStore || defaultSelectionStore

  const { isOpen, latlng, isMenuOpen, activeModal, sourceItem } = currentUiStore.quickCreate

  const containerRef = useRef(null)

  const [marker] = useState(() => new MarkerModel())
  const [pointBuffer] = useState(() => new PointBuffer())
  const [isochrone] = useState(() => new Isochrone())

  useEffect(() => {
    if (latlng) {
      const latVal = typeof latlng.lat === 'number' ? latlng.lat : (latlng[0] || 0)
      const lngVal = typeof latlng.lng === 'number' ? latlng.lng : (latlng[1] || 0)

      const defaultColorId = 1
      const sourceName = (sourceItem && (sourceItem.name || sourceItem.title || sourceItem.label))
        ? (sourceItem.name || sourceItem.title || sourceItem.label).trim()
        : ''

      marker.name = ''
      marker.position.latitude = latVal
      marker.position.longitude = lngVal
      marker.position.latLng = { lat: latVal, lng: lngVal }
      marker.color_id = (sourceItem && sourceItem.color_id) || defaultColorId
      marker.description = (sourceItem && sourceItem.description) || ''
      marker.initial = false
      marker.invalid.clear()

      pointBuffer.name = sourceName ? `Buffer around ${sourceName}` : ''
      pointBuffer.position.latitude = latVal
      pointBuffer.position.longitude = lngVal
      pointBuffer.position.latLng = { lat: latVal, lng: lngVal }
      pointBuffer.buffer = 10
      pointBuffer.bufferUnit = 'km'
      pointBuffer.color_id = (sourceItem && sourceItem.color_id) || defaultColorId
      pointBuffer.initial = false
      pointBuffer.invalid.clear()

      isochrone.name = sourceName ? `Isochrone around ${sourceName}` : ''
      isochrone.position.latitude = latVal
      isochrone.position.longitude = lngVal
      isochrone.position.latLng = { lat: latVal, lng: lngVal }
      isochrone.modeOfTransportation = 'Driving'
      isochrone.travelTime = 15
      isochrone.color_id = (sourceItem && sourceItem.color_id) || defaultColorId
      isochrone.initial = false
      isochrone.enableSubmit = true
      isochrone.invalid.clear()
    }
  }, [latlng, sourceItem])

  const ensureVisible = useCallback(() => {
    if (!containerRef.current) return
    const leafletMap = (currentUiStore.map && currentUiStore.map.leafletElement) || null
    if (!leafletMap) return

    const rect = containerRef.current.getBoundingClientRect()
    const mapEl = leafletMap.getContainer()
    const mapRect = mapEl
      ? mapEl.getBoundingClientRect()
      : { top: 0, bottom: window.innerHeight, left: 0, right: window.innerWidth }

    // Clearance margins
    const topMargin = 70
    const bottomMargin = 25
    const leftMargin = 330
    const rightMargin = 25

    let panX = 0
    let panY = 0

    if (rect.top < mapRect.top + topMargin) {
      panY = rect.top - (mapRect.top + topMargin)
    } else if (rect.bottom > mapRect.bottom - bottomMargin) {
      panY = rect.bottom - (mapRect.bottom - bottomMargin)
    }

    if (rect.left < mapRect.left + leftMargin) {
      panX = rect.left - (mapRect.left + leftMargin)
    } else if (rect.right > mapRect.right - rightMargin) {
      panX = rect.right - (mapRect.right - rightMargin)
    }

    if (Math.abs(panX) > 2 || Math.abs(panY) > 2) {
      leafletMap.panBy([panX, panY], { animate: true, duration: 0.25 })
    }
  }, [currentUiStore])

  useEffect(() => {
    if (isOpen && latlng) {
      ensureVisible()
      const t1 = setTimeout(ensureVisible, 60)
      const t2 = setTimeout(ensureVisible, 200)
      return () => {
        clearTimeout(t1)
        clearTimeout(t2)
      }
    }
  }, [isOpen, latlng, isMenuOpen, activeModal, currentUiStore.isColorPickerExpanded, ensureVisible])

  if (!isOpen || !latlng) {
    return null
  }

  const lat = typeof latlng.lat === 'number' ? latlng.lat : (latlng[0] || 0)
  const lng = typeof latlng.lng === 'number' ? latlng.lng : (latlng[1] || 0)

  const group = currentUiStore.resolveTargetGroup(
    currentSelectionStore.selectedTargetGroup?.item ||
    currentSelectionStore.data?.group ||
    (currentUiStore.displayGroups && currentUiStore.displayGroups[0]) ||
    (currentDataStore.targetGroups && currentDataStore.targetGroups[0])
  )

  const handleClose = () => {
    currentUiStore.closeQuickCreate()
  }

  return (
    <Marker
      position={[lat, lng]}
      icon={quickCreateMarkerIcon}
      interactive
    >
      <Tooltip
        position={[lat, lng]}
        className='quick-create-leaflet-tooltip'
        direction='top'
        offset={[0, -10]}
        permanent
        interactive
      >
        <Provider
          uiStore={currentUiStore}
          dataStore={currentDataStore}
          selectionStore={currentSelectionStore}
        >
          <div
            className='quick-create-wrapper'
            ref={containerRef}
            onClick={e => e.stopPropagation()}
            onMouseDown={e => e.stopPropagation()}
            onMouseUp={e => e.stopPropagation()}
            onDoubleClick={e => e.stopPropagation()}
            refCallback={el => {
              if (el) {
                L.DomEvent.disableClickPropagation(el)
                L.DomEvent.disableScrollPropagation(el)
              }
            }}
          >
            {/* Top Card + Action Menu */}
            {!activeModal && (
              <>
                <div className='quick-create-top-card'>
                  <div className='quick-create-top-card__pin-icon'>
                    <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='#292C44' strokeWidth='1.7' strokeLinecap='round' strokeLinejoin='round'>
                      <path d='M12 21s-6-5.686-6-10a6 6 0 0 1 12 0c0 4.314-6 10-6 10z' />
                      <circle cx='12' cy='11' r='2.5' />
                    </svg>
                  </div>
                  <div className='quick-create-top-card__info'>
                    <span className='quick-create-top-card__title'>
                      {sourceItem ? (sourceItem.name || 'Selected point') : 'New point'}
                    </span>
                    <span className='quick-create-top-card__coords'>
                      {lat.toFixed(4)}, {lng.toFixed(4)}
                    </span>
                  </div>
                  <button
                    type='button'
                    className={`quick-create-top-card__plus-btn ${isMenuOpen ? 'active' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation()
                      currentUiStore.toggleQuickCreateMenu()
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    title='Quick Create Actions'
                  >
                    <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='#292C44' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                      <line x1='12' y1='5' x2='12' y2='19' />
                      <line x1='5' y1='12' x2='19' y2='12' />
                    </svg>
                  </button>
                </div>

                {isMenuOpen && (
                  <div className='quick-create-menu-card'>
                    {!sourceItem && (
                      <button
                        type='button'
                        className='quick-create-menu-card__item quick-create-menu-card__item--active'
                        onClick={(e) => {
                          e.stopPropagation()
                          currentUiStore.openQuickCreateModal('point')
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}
                      >
                        <span className='quick-create-menu-card__icon'>
                          <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='#1e293b' strokeWidth='1.7' strokeLinecap='round' strokeLinejoin='round'>
                            <path d='M12 21s-6-5.686-6-10a6 6 0 0 1 12 0c0 4.314-6 10-6 10z' />
                            <circle cx='12' cy='11' r='2.5' />
                          </svg>
                        </span>
                        <span className='quick-create-menu-card__label'>Save Point</span>
                      </button>
                    )}

                    <button
                      type='button'
                      className={`quick-create-menu-card__item ${sourceItem ? 'quick-create-menu-card__item--active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        currentUiStore.openQuickCreateModal('buffer')
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      onTouchStart={(e) => e.stopPropagation()}
                    >
                      <span className='quick-create-menu-card__icon'>
                        <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='#1e293b' strokeWidth='1.7' strokeLinecap='round' strokeLinejoin='round'>
                          <circle cx='12' cy='12' r='8.5' strokeDasharray='3.5 3.5' />
                          <circle cx='12' cy='12' r='2' fill='#1e293b' />
                        </svg>
                      </span>
                      <span className='quick-create-menu-card__label'>Create Buffer</span>
                    </button>

                    <button
                      type='button'
                      className='quick-create-menu-card__item'
                      onClick={(e) => {
                        e.stopPropagation()
                        currentUiStore.openQuickCreateModal('isochrone')
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      onTouchStart={(e) => e.stopPropagation()}
                    >
                      <span className='quick-create-menu-card__icon'>
                        <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='#1e293b' strokeWidth='1.7' strokeLinecap='round' strokeLinejoin='round'>
                          <circle cx='12' cy='12' r='8.5' />
                          <polyline points='12 7.5 12 12 15 15' />
                        </svg>
                      </span>
                      <span className='quick-create-menu-card__label'>Create Isochrone</span>
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Reusing existing MarkerPane */}
            {activeModal === 'point' && (
              <div className='quick-create-pane-wrapper'>
                <Icon
                  onClick={handleClose}
                  extraClasses={['close']}
                  id='fa-times'
                />
                <MarkerPane
                  isQuickCreate
                  marker={marker}
                  group={group}
                  onCancel={handleClose}
                  onSave={handleClose}
                />
              </div>
            )}

            {/* Reusing existing PointBufferPane */}
            {activeModal === 'buffer' && (
              <div className='quick-create-pane-wrapper'>
                <Icon
                  onClick={handleClose}
                  extraClasses={['close']}
                  id='fa-times'
                />
                <PointBufferPane
                  isQuickCreate
                  pointBuffer={pointBuffer}
                  group={group}
                  onCancel={handleClose}
                  onSave={handleClose}
                />
              </div>
            )}

            {/* Reusing existing IsochronePane */}
            {activeModal === 'isochrone' && (
              <div className='quick-create-pane-wrapper'>
                <Icon
                  onClick={handleClose}
                  extraClasses={['close']}
                  id='fa-times'
                />
                <IsochronePane
                  isQuickCreate
                  isochrone={isochrone}
                  group={group}
                  onCancel={handleClose}
                  onSave={handleClose}
                />
              </div>
            )}
          </div>
        </Provider>
      </Tooltip>
    </Marker>
  )
}

export default withTranslation()(inject('uiStore', 'selectionStore', 'dataStore')(observer(QuickCreateTooltip)))
